import type { Graph } from "./graph";

export interface EdgeHit {
  edgeId: number;
  t: number;
  x: number;
  y: number;
  dist: number;
}

export function nearestNode(g: Graph, x: number, y: number, r: number, exclude?: Set<number>): number | null {
  let best: number | null = null;
  let bestD = r * r;
  for (const n of g.nodes.values()) {
    if (exclude?.has(n.i)) continue;
    const d = (n.x - x) ** 2 + (n.y - y) ** 2;
    if (d <= bestD) {
      bestD = d;
      best = n.i;
    }
  }
  return best;
}

export function nearestEdge(g: Graph, x: number, y: number, r: number, excludeEdges?: Set<number>): EdgeHit | null {
  let best: EdgeHit | null = null;
  for (const e of g.edges.values()) {
    if (excludeEdges?.has(e.i)) continue;
    const a = g.nodes.get(e.a)!;
    const b = g.nodes.get(e.b)!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    let t = len2 > 0 ? ((x - a.x) * dx + (y - a.y) * dy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const px = a.x + t * dx;
    const py = a.y + t * dy;
    const d = Math.hypot(px - x, py - y);
    if (d <= r && (best === null || d < best.dist)) {
      best = { edgeId: e.i, t, x: px, y: py, dist: d };
    }
  }
  return best;
}

/** Ray-casting point-in-polygon test. */
export function pointInPolygon(pts: { x: number; y: number }[], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y;
    const xj = pts[j].x, yj = pts[j].y;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export interface Intersection {
  edgeId: number;
  t: number; // position along the query segment (0..1)
  x: number;
  y: number;
}

/** Proper crossings between segment (p1->p2) and existing edges; endpoint touches excluded. */
export function segmentIntersections(
  g: Graph,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  skipNodes: Set<number>,
  skipEdges: Set<number>,
): Intersection[] {
  const out: Intersection[] = [];
  const rx = x2 - x1;
  const ry = y2 - y1;
  const EPS = 1e-6;
  for (const e of g.edges.values()) {
    if (skipEdges.has(e.i)) continue;
    if (skipNodes.has(e.a) || skipNodes.has(e.b)) continue;
    const a = g.nodes.get(e.a)!;
    const b = g.nodes.get(e.b)!;
    const sx = b.x - a.x;
    const sy = b.y - a.y;
    const denom = rx * sy - ry * sx;
    if (Math.abs(denom) < EPS) continue; // parallel
    const qx = a.x - x1;
    const qy = a.y - y1;
    const t = (qx * sy - qy * sx) / denom;
    const u = (qx * ry - qy * rx) / denom;
    if (t > EPS && t < 1 - EPS && u > EPS && u < 1 - EPS) {
      out.push({ edgeId: e.i, t, x: x1 + t * rx, y: y1 + t * ry });
    }
  }
  out.sort((i1, i2) => i1.t - i2.t);
  return out;
}
